import { createContext, useContext, useState, useEffect } from 'react'
import api from '@helpers/api.js'
import { useAuth } from '@contexts/AuthContext'

const ChatPanelContext = createContext({
    open: false,
    toggle: () => {},
    close: () => {},
    chats: [],
    loading: true,
    totalUnread: 0,
    activeChat: null,
    openChat: () => {},
    clearActiveChat: () => {},
})

function normalizeChatInfo(chatInfo = {}) {
    const participant =
        chatInfo.user ||
        chatInfo.counterpart ||
        chatInfo.participant ||
        chatInfo.member ||
        {}

    return {
        ...chatInfo,
        user_id: chatInfo.user_id ?? participant.id ?? chatInfo.receiver_id,
        username:
            chatInfo.username ??
            participant.username ??
            participant.name ??
            participant.nickname,
        avatar: chatInfo.avatar ?? participant.avatar,
        email: chatInfo.email ?? participant.email,
    }
}

export function ChatPanelProvider({ children }) {
    const [open, setOpen] = useState(false)
    const [chats, setChats] = useState([])
    const [loading, setLoading] = useState(true)
    const [activeChat, setActiveChat] = useState(null)
    const { user } = useAuth()

    const totalUnread = chats.reduce((acc, c) => acc + (c.unread_messages || 0), 0)

    useEffect(() => {
        if (!user) return
        loadChats()
        const interval = setInterval(loadChats, 30000)
        return () => clearInterval(interval)
    }, [user])

    // Listen for sendMessageTo events dispatched from anywhere in the app
    useEffect(() => {
        function handler(e) {
            openChat(e.detail)
        }
        window.addEventListener('start_chat_with', handler)
        return () => window.removeEventListener('start_chat_with', handler)
    }, [])

    // Handle ?chat=userId URL param on load
    useEffect(() => {
        if (loading) return
        const params = new URLSearchParams(window.location.search)
        const chatUserId = params.get('chat')
        if (!chatUserId) return

        const found = chats.find(c => (
            String(c.user_id) === String(chatUserId) ||
            String(c.user?.id) === String(chatUserId) ||
            String(c.counterpart?.id) === String(chatUserId)
        ))
        openChat(found || { user_id: chatUserId, username: 'Usuário', avatar: '', email: '' })
    }, [loading])

    async function loadChats() {
        try {
            const { data } = await api.get('chat')
            const list = Array.isArray(data) ? data : data?.data || data?.chats || []
            setChats(list.map(normalizeChatInfo))
        } catch {}
        setLoading(false)
    }

    function openChat(chatInfo) {
        setActiveChat(normalizeChatInfo(chatInfo))
        setOpen(true)
    }

    return (
        <ChatPanelContext.Provider value={{
            open,
            toggle: () => setOpen(p => !p),
            close: () => setOpen(false),
            chats,
            loading,
            totalUnread,
            activeChat,
            openChat,
            clearActiveChat: () => setActiveChat(null),
        }}>
            {children}
        </ChatPanelContext.Provider>
    )
}

export const useChatPanel = () => useContext(ChatPanelContext)

// Drop-in replacement for the old sendMessageTo from contexts/ChatContext
export function sendMessageTo(userInfo) {
    const detail = normalizeChatInfo({ ...userInfo, user_id: userInfo.user_id ?? userInfo.id, id: undefined })
    window.dispatchEvent(new CustomEvent('start_chat_with', { detail }))
}
